import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button } from '@mui/material';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from 'store';
import { isOperatorOrHigher } from 'features/auth/helpers';
import { useCurrentUserTeam } from 'features/auth/hooks/useCurrentUserTeam';
import { CountryCodeEnum, RolesEnum } from 'features/common/types';
import { SALES_TEAMS } from 'utils/slug';
import routes from 'constants/routes';

export interface QuickLinkItem {
	icon: string;
	label: string;
	href: string;
	isExternal?: boolean;
}

const BANK_ACCOUNTS_BY_COUNTRY: Partial<Record<CountryCodeEnum, string>> = {
	[CountryCodeEnum.URUGUAY]: 'https://cuentas.mevuelo.com/uy',
	[CountryCodeEnum.PARAGUAY]: 'https://cuentas.mevuelo.com/py',
	[CountryCodeEnum.BOLIVIA]: 'https://cuentas.mevuelo.com/bo',
	[CountryCodeEnum.COLOMBIA]: 'https://cuentas.mevuelo.com/co',
};

const buttonSx = {
	whiteSpace: 'nowrap' as const,
	flexShrink: 0,
	textTransform: 'none' as const,
	fontSize: '0.75rem',
	px: 1.5,
	py: 0.5,
	borderColor: 'divider',
	color: 'text.primary',
	'&:hover': { borderColor: 'primary.main', color: 'primary.main' },
};

function QuickLinkButton({ item }: { item: QuickLinkItem }): JSX.Element {
	const startIcon = <span style={{ fontSize: '1rem' }}>{item.icon}</span>;

	if (item.isExternal) {
		return (
			<Button
				size="small"
				variant="outlined"
				component="a"
				href={item.href}
				target="_blank"
				rel="noopener noreferrer"
				sx={buttonSx}
				startIcon={startIcon}
			>
				{item.label}
			</Button>
		);
	}

	return (
		<Button size="small" variant="outlined" component={RouterLink} to={item.href} sx={buttonSx} startIcon={startIcon}>
			{item.label}
		</Button>
	);
}

interface QuickLinksBarProps {
	/** Si se pasa, usa esa lista. Si no, la construye automáticamente según rol/país. */
	items?: QuickLinkItem[];
	sx?: Record<string, unknown>;
}

/**
 * Barra de accesos rápidos horizontal con scroll.
 * Sin props → construye los items según el rol y país del usuario logueado.
 */
export function QuickLinksBar({ items: itemsProp, sx }: QuickLinksBarProps): JSX.Element {
	const auth = useSelector((state: RootState) => state.auth, shallowEqual);
	const { team } = useCurrentUserTeam();

	const autoItems = useMemo((): QuickLinkItem[] => {
		const items: QuickLinkItem[] = [
			{ icon: '📝', label: 'Crear coti', href: '/quotations/new' },
			{ icon: '🛒', label: 'Crear orden', href: '/salesorder/new' },
			{ icon: '🏝️', label: 'Paquetes', href: '/stats/packages' },
			{ icon: '📅', label: 'Calendario de viajes', href: '/salesorder/calendar' },
			{ icon: '🧰', label: 'Sales tools', href: routes.salesTools.root },
		];

		const bankUrl = BANK_ACCOUNTS_BY_COUNTRY[auth.country];
		if (bankUrl) {
			items.push({ icon: '💰', label: 'Cuentas bancarias', href: bankUrl, isExternal: true });
		}

		if (isOperatorOrHigher(auth.user)) {
			items.push({
				icon: '💳',
				label: 'Pago de servicio',
				href: 'https://forms.gle/JSpyosUzdbv1ySfB6',
				isExternal: true,
			});
		}

		const hasSalesTeam = !!team && (SALES_TEAMS as readonly string[]).includes(team);
		const isSeller = auth.user?.role === RolesEnum.SELLER;

		// Dashboard personal: solo para quien realmente tiene ventas propias
		// (vendedor, o admin/superadmin de un equipo de ventas). Un superadmin
		// no-ventas (ej. DIRECCION) no lo ve porque estaría vacío.
		if (auth.user?.name && (isSeller || hasSalesTeam)) {
			items.push({ icon: '📊', label: 'Dashboard', href: routes.salesDashboard.seller(auth.user.name) });
		}

		return items;
	}, [auth.country, auth.user, team]);

	const items = itemsProp ?? autoItems;

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'row',
				flexWrap: 'nowrap',
				justifyContent: 'center',
				overflowX: 'auto',
				gap: 1,
				mb: 2,
				pb: 0.5,
				'&::-webkit-scrollbar': { height: '4px' },
				'&::-webkit-scrollbar-thumb': { backgroundColor: '#c1c1c1', borderRadius: '4px' },
				...sx,
			}}
		>
			{items.map((item) => (
				<QuickLinkButton key={`${item.href}-${item.label}`} item={item} />
			))}
		</Box>
	);
}
